// useFileDrop — DnD attachment hook for the desktop Beam workspace (F11.3).
//
// Returns `{ dragOverProps, active }`. Spread `dragOverProps` onto the target
// element to receive `dragenter / dragover / dragleave / drop` events:
//
//   const { dragOverProps, active } = useFileDrop({ onDrop });
//   <div {...dragOverProps}>{active && <DropZone />}</div>
//
// Quirks this handles:
//   - Chrome fires `dragleave` on every nested-element transition. We use the
//     canonical counter pattern (increment on enter, decrement on leave) so
//     `active` stays stable as the cursor moves between child elements.
//   - `dragover.preventDefault()` is mandatory — otherwise the browser cancels
//     the drop and `onDrop` never fires.
//   - `e.dataTransfer.types` carries the MIME-ish list of payloads on the
//     drag; we only react to drags that carry `Files`. Text drags from
//     elsewhere on the page are ignored so the overlay doesn't flash when a
//     user drags a link off-site.
//   - Touch devices don't have a real drag-drop UX. We detect them via
//     `'ontouchstart' in window` and return a no-op handler bag — the spec
//     calls for the mobile ShareSheet flow on touch instead.
//
// Options:
//   onDrop({ files: File[] })  — called with the captured files, capped at
//                                `maxFiles` and filtered through `accept`.
//   accept(file): boolean       — optional predicate. Files that fail are
//                                dropped silently before `onDrop` sees them.
//   maxFiles: number            — defaults to 20. Anything beyond is sliced
//                                off; the caller can decide whether to toast.

import { useCallback, useMemo, useRef, useState } from 'react';

const DEFAULT_MAX_FILES = 20;

function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window;
}

function hasFiles(dataTransfer) {
  if (!dataTransfer) return false;
  const types = dataTransfer.types;
  if (!types) return false;
  // `types` is a DOMStringList on real events and a plain array in tests.
  // Both support `.includes` (DOMStringList does via iteration in modern
  // browsers; we fall back to a manual scan to stay safe).
  if (typeof types.includes === 'function') return types.includes('Files');
  for (let i = 0; i < types.length; i++) {
    if (types[i] === 'Files') return true;
  }
  return false;
}

export function useFileDrop({
  onDrop,
  accept,
  maxFiles = DEFAULT_MAX_FILES,
} = {}) {
  // The counter survives child enter/leave transitions. We mirror it into
  // state so the consumer re-renders on edge changes. `useRef` alone wouldn't
  // trigger renders; `useState` alone would race when entering quickly.
  const counterRef = useRef(0);
  const [active, setActive] = useState(false);

  // Stash the latest callbacks in refs so the returned event handlers stay
  // stable across renders (consumers spreading them onto JSX don't need to
  // re-bind every tick).
  const onDropRef = useRef(onDrop);
  const acceptRef = useRef(accept);
  onDropRef.current = onDrop;
  acceptRef.current = accept;

  const touch = isTouchDevice();

  const reset = useCallback(() => {
    counterRef.current = 0;
    setActive(false);
  }, []);

  const handleDragEnter = useCallback((e) => {
    if (!hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    counterRef.current += 1;
    if (counterRef.current === 1) setActive(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    if (!hasFiles(e.dataTransfer)) return;
    // Required for `drop` to fire. Without this the browser falls back to
    // its default handler (open the file in a new tab).
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    counterRef.current = Math.max(0, counterRef.current - 1);
    if (counterRef.current === 0) setActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      if (!hasFiles(e.dataTransfer)) return;
      e.preventDefault();
      reset();

      const list = e.dataTransfer && e.dataTransfer.files;
      const all = list ? Array.from(list) : [];
      const filtered =
        typeof acceptRef.current === 'function'
          ? all.filter((f) => {
              try {
                return !!acceptRef.current(f);
              } catch {
                return false;
              }
            })
          : all;
      const cap = Number.isFinite(maxFiles) && maxFiles > 0 ? maxFiles : DEFAULT_MAX_FILES;
      const files = filtered.slice(0, cap);

      if (typeof onDropRef.current === 'function') {
        try {
          onDropRef.current({ files });
        } catch {
          /* one bad consumer shouldn't break the drag pipeline */
        }
      }
    },
    [maxFiles, reset],
  );

  const dragOverProps = useMemo(() => {
    if (touch) return {};
    return {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    };
  }, [touch, handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  if (touch) {
    return { dragOverProps: {}, active: false };
  }

  return { dragOverProps, active };
}

export default useFileDrop;
